import { useEffect, useRef, useCallback } from 'react';
import logger from 'itrvl-logger';
import { useUser } from 'common/context/UserContext';
import { useApi } from 'common/hooks/api';

const log = logger(__filename);

function useUnreadTitleAlert() {
  const unreadCount = useRef(0);

  const updateTitle = useCallback(count => {
    if (count > 0) {
      const base = document.title.replace(/^\(\d+\)\s*/, '');
      document.title = `(${count}) ${base}`;
    } else {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    }
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      updateTitle(unreadCount.current);
    };

    const onMessage = event => {
      if (!event.origin.endsWith('.featurebase.app')) return;
      const data = event.data;
      if (data?.type === 'UNREAD_COUNT_CHANGE') {
        const count = data.unreadCount ?? 0;
        unreadCount.current = count;
        updateTitle(count);
      }
      if (data?.type === 'NOTIFICATION_AVAILABLE') {
        unreadCount.current = Math.max(unreadCount.current, 1);
        updateTitle(unreadCount.current);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onMessage);
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [updateTitle]);
}

function loadSdk() {
  const id = 'featurebase-sdk';
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.src = 'https://do.featurebase.app/js/sdk.js';
  document.head.appendChild(s);
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = function () {
      (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
    };
  }
}

function Featurebase() {
  const { user } = useUser();
  const Api = useApi();
  const hasBooted = useRef(false);
  const bootIdRef = useRef(0);
  useUnreadTitleAlert();

  const shutdown = useCallback(() => {
    bootIdRef.current++;
    if (hasBooted.current) {
      window.Featurebase('shutdown');
      hasBooted.current = false;
    }
  }, []);

  const bootAuthenticated = useCallback(
    async currentBootId => {
      try {
        const { data } = await Api.get('/api/Info/featurebaseToken');
        if (currentBootId !== bootIdRef.current) return;
        const { featurebaseJwt } = data;

        window.Featurebase(
          'identify',
          {
            organization: process.env.REACT_APP_FEATUREBASE_ORG,
            featurebaseJwt,
          },
          err => {
            if (err) log.warn('[featurebase] identify failed', err);
          },
        );

        if (currentBootId !== bootIdRef.current) return;

        window.Featurebase('boot', {
          appId: process.env.REACT_APP_FEATUREBASE_APP_ID,
          featurebaseJwt,
          theme: 'light',
          language: 'en',
        });
        hasBooted.current = true;
      } catch (err) {
        if (currentBootId !== bootIdRef.current) return;
        log.warn('[featurebase] JWT fetch failed', err);
      }
    },
    [Api],
  );

  useEffect(() => {
    loadSdk();
  }, []);

  useEffect(() => {
    shutdown();

    if (user?.userId) {
      const currentBootId = ++bootIdRef.current;
      bootAuthenticated(currentBootId);
    }

    return shutdown;
  }, [user?.userId, shutdown, bootAuthenticated]);

  return null;
}

export default Featurebase;
